import { head } from "lodash";
import get from "lodash/get";
import { EVBASIC_TOKEN_KEY, EVCORE_TOKEN_KEY } from "@/constants/repositories";
import type { DefaultErrors } from "@/types/shared";
import { getStoredLanguage } from "@/utils/language";
import { getRealmToken, hasToken } from "@/utils/repositories";
import { handleApiError } from "@/utils/shared";
import type { ApiResponse, BaseRepository } from "./common-types";

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface NetworkConnectionInfo {
  effectiveType: string;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
}

interface ApiRequestContext {
  browserOnline: "true" | "false" | "unknown";
  endpoint: string;
  method: string;
  realm: string | null;
  url: string;
  requestStartTime: number;
  requestDurationMs: number | null;
  bodySize: number | null;
  networkConnection: NetworkConnectionInfo | null;
}

// Custom error interface for API errors
interface ApiError extends Error {
  response?: {
    status: number;
    data?: ApiErrorResponseBody;
  };
  isAxiosError?: boolean;
  requestContext?: ApiRequestContext;
}

interface ApiErrorResponseBody {
  message?: string;
  error?: string;
  messages?: CoreMessageError[];
  code?: string;
  type?: string;
  details?: Record<string, string | number | boolean | null>;
  data?:
    | CoreMessageError
    | CoreMessageError[]
    | Record<string, string | number | boolean | null>
    | string
    | null;
}

interface CoreMessageError {
  message?: string;
}

/**
 * Base repository class for API interactions
 */
export class BaseRepositoryV2 implements BaseRepository {
  protected baseUrl: string;

  constructor(baseUrl?: string) {
    // Use provided baseUrl or fallback to environment variable or empty string
    this.baseUrl =
      baseUrl || (typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_URL : "") || "";
  }

  /**
   * Wrapper for API calls with error handling
   * Automatically handles 401/403 status codes by redirecting to logout
   */
  protected async handleApiCall<T>(
    apiCall: (setRequestContext: (requestContext: ApiRequestContext) => void) => Promise<T>
  ): Promise<T> {
    let requestContext: ApiRequestContext | undefined;

    try {
      return await apiCall((nextRequestContext) => {
        requestContext = nextRequestContext;
      });
    } catch (error) {
      if (requestContext) {
        requestContext.requestDurationMs = Date.now() - requestContext.requestStartTime;
        requestContext.networkConnection = this.getNetworkConnectionInfo();
      }

      const enrichedError = requestContext
        ? this.enrichErrorWithRequestContext(error, requestContext)
        : this.normalizeError(error);

      // Handle 401/403 status codes automatically
      const defaultErrors: DefaultErrors = {
        responseError: "Server error occurred",
        requestError: "Network error occurred",
        unknownError: "Unknown error occurred",
      };

      const apiError = handleApiError(enrichedError, defaultErrors);

      if (apiError.status === 401 || apiError.status === 403) {
        if (typeof window !== "undefined") {
          window.location.href = "/auth/callback/signout";
        }
        // Don't re-throw the error for 401/403 to prevent further execution
        // Return a promise that never resolves to prevent the calling code from continuing
        return new Promise<T>(() => {
          // Intentionally empty - this promise never resolves to halt execution
        });
      }

      throw enrichedError;
    }
  }

  /**
   * Get basic headers for API calls
   */
  protected async getHeaders(): Promise<Record<string, string>> {
    const lng = getStoredLanguage();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json; charset=utf-8",
      "Accept-Language": lng,
    };
    return headers;
  }

  /**
   * Get authentication headers with token
   */
  protected async getAuthHeaders(skipContentType = false): Promise<Record<string, string>> {
    const lng = getStoredLanguage();
    const baseHeaders = skipContentType ? {} : await this.getHeaders();
    const evbasicToken = localStorage.getItem(EVBASIC_TOKEN_KEY);
    const evcoreToken = localStorage.getItem(EVCORE_TOKEN_KEY);

    if (!hasToken({ evbasicToken, evcoreToken })) {
      if (typeof window !== "undefined" && !window.location.pathname.includes("/auth/")) {
        window.location.href = "/auth/callback/signin";
      }
      return new Promise<Record<string, string>>(() => {
        // Intentionally empty - halts execution while redirect occurs
      });
    }

    const persistedBoundStore = localStorage.getItem("persisted-bound-store");
    const realm = get(JSON.parse(persistedBoundStore || "{}"), "state.realm");
    const realmToken = getRealmToken({ evbasicToken, evcoreToken, realm });
    const realmAccessToken = get(JSON.parse(realmToken || "{}"), "access_token");

    if (!realmAccessToken) {
      if (typeof window !== "undefined" && !window.location.pathname.includes("/auth/")) {
        window.location.href = "/auth/callback/signin";
      }
      return new Promise<Record<string, string>>(() => {
        // Intentionally empty - halts execution while redirect occurs
      });
    }

    return {
      ...baseHeaders,
      Authorization: `Bearer ${realmAccessToken}`,
      "Accept-Language": lng,
      Accept: "application/json; charset=utf-8",
      "x-provider-realm": realm,
      Realm: realm,
    };
  }

  /**
   * Ensures proper URL formatting by adding leading slash if needed
   */
  private formatUrl(endpoint: string): string {
    const cleanBaseUrl = this.baseUrl.replace(/\/+$/, "");
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${cleanBaseUrl}${cleanEndpoint}`;
  }

  private getBrowserOnlineState(): "true" | "false" | "unknown" {
    if (typeof navigator === "undefined") {
      return "unknown";
    }

    return navigator.onLine ? "true" : "false";
  }

  private getNetworkConnectionInfo(): NetworkConnectionInfo | null {
    if (typeof navigator === "undefined") {
      return null;
    }

    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!connection) {
      return null;
    }

    return {
      effectiveType: connection.effectiveType || "unknown",
      downlink: connection.downlink ?? null,
      rtt: connection.rtt ?? null,
      saveData: connection.saveData || false,
    };
  }

  private getRealmFromHeaders(headers: Record<string, string>): string | null {
    return headers.Realm || headers["x-provider-realm"] || null;
  }

  private buildRequestContext(
    endpoint: string,
    method: string,
    headers: Record<string, string>,
    bodySize: number | null = null
  ): ApiRequestContext {
    return {
      browserOnline: this.getBrowserOnlineState(),
      endpoint: endpoint.startsWith("/") ? endpoint : `/${endpoint}`,
      method: method.toUpperCase(),
      realm: this.getRealmFromHeaders(headers),
      url: this.formatUrl(endpoint),
      requestStartTime: Date.now(),
      requestDurationMs: null,
      bodySize,
      networkConnection: this.getNetworkConnectionInfo(),
    };
  }

  private normalizeError(error: unknown): ApiError {
    if (error instanceof Error) {
      return error as ApiError;
    }

    return new Error(get(error, "message", "Unknown error occurred")) as ApiError;
  }

  private enrichErrorWithRequestContext(
    error: unknown,
    requestContext: ApiRequestContext
  ): ApiError {
    const normalizedError = this.normalizeError(error);
    normalizedError.requestContext = requestContext;
    return normalizedError;
  }

  /**
   * Creates and throws a standardized API error with proper response structure
   */
  private async createApiError(response: Response): Promise<never> {
    const responseText = await response.text();
    let responseBody: ApiErrorResponseBody = {};
    if (responseText.trim()) {
      responseBody = JSON.parse(responseText) as ApiErrorResponseBody;
    }

    let message = responseBody.message || responseBody.error || "";
    if (Array.isArray(responseBody.messages)) {
      message = head(responseBody.messages as CoreMessageError[])?.message || "";
    }
    if (!message) {
      message = response.statusText;
    }
    if (!message) {
      message = `HTTP ${response.status}`;
    }

    const error = new Error(message) as ApiError;
    error.response = {
      status: response.status,
      data: responseBody,
    };
    throw error;
  }

  /**
   * GET request with error handling
   */
  public async get<_T>(
    endpoint: string,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      const headers = auth
        ? { ...(await this.getAuthHeaders()), ...customHeaders }
        : { ...(await this.getHeaders()), ...customHeaders };
      setRequestContext(this.buildRequestContext(endpoint, "GET", headers));
      const response = await fetch(this.formatUrl(endpoint), {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        await this.createApiError(response);
      }

      return response;
    });
  }

  /**
   * POST request with error handling
   */
  public async post<T = Record<string, string>>(
    endpoint: string,
    data: T,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      const headers = auth
        ? { ...(await this.getAuthHeaders()), ...customHeaders }
        : { ...(await this.getHeaders()), ...customHeaders };
      const body = data !== undefined ? JSON.stringify(data) : undefined;
      setRequestContext(this.buildRequestContext(endpoint, "POST", headers, body?.length ?? null));
      const response = await fetch(this.formatUrl(endpoint), {
        method: "POST",
        headers,
        body,
      });

      if (!response.ok) {
        await this.createApiError(response);
      }

      return response;
    });
  }

  /**
   * PUT request with error handling
   */
  public async put<T = Record<string, string>>(
    endpoint: string,
    data: T,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      const headers = auth
        ? { ...(await this.getAuthHeaders()), ...customHeaders }
        : { ...(await this.getHeaders()), ...customHeaders };
      const body = data !== undefined ? JSON.stringify(data) : undefined;
      setRequestContext(this.buildRequestContext(endpoint, "PUT", headers, body?.length ?? null));
      const response = await fetch(this.formatUrl(endpoint), {
        method: "PUT",
        headers,
        body,
      });

      if (!response.ok) {
        await this.createApiError(response);
      }

      return response;
    });
  }

  /**
   * PATCH request with error handling
   */
  public async patch<T = Record<string, string>>(
    endpoint: string,
    data: T,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      const headers = auth
        ? { ...(await this.getAuthHeaders()), ...customHeaders }
        : { ...(await this.getHeaders()), ...customHeaders };
      const body = data !== undefined ? JSON.stringify(data) : undefined;
      setRequestContext(this.buildRequestContext(endpoint, "PATCH", headers, body?.length ?? null));
      const response = await fetch(this.formatUrl(endpoint), {
        method: "PATCH",
        headers,
        body,
      });

      if (!response.ok) {
        await this.createApiError(response);
      }

      return response;
    });
  }

  /**
   * DELETE request with error handling
   */
  public async delete<_T>(
    endpoint: string,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      const headers = auth
        ? { ...(await this.getAuthHeaders()), ...customHeaders }
        : { ...(await this.getHeaders()), ...customHeaders };
      setRequestContext(this.buildRequestContext(endpoint, "DELETE", headers));
      const response = await fetch(this.formatUrl(endpoint), {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await this.createApiError(response);
      }

      return response;
    });
  }

  /**
   * Custom endpoint call with custom headers
   */
  protected async customCall<_T>(
    endpoint: string,
    options: RequestInit,
    customHeaders: Record<string, string> = {},
    auth = false
  ): Promise<Response> {
    return this.handleApiCall(async (setRequestContext) => {
      // Check if body is FormData to avoid setting Content-Type
      const isFormData = options.body instanceof FormData;
      const lng = getStoredLanguage();
      const requestMethod = options.method || "GET";

      // Handle FormData with auth
      if (isFormData && auth) {
        const authHeaders = await this.getAuthHeaders(true);
        const headers = { ...authHeaders, ...customHeaders };
        setRequestContext(this.buildRequestContext(endpoint, requestMethod, headers));
        return this.executeRequest(endpoint, options, headers);
      }

      // Handle FormData without auth
      if (isFormData) {
        const headers = {
          Accept: "application/json; charset=utf-8",
          "Accept-Language": lng,
          ...customHeaders,
        };
        setRequestContext(this.buildRequestContext(endpoint, requestMethod, headers));
        return this.executeRequest(endpoint, options, headers);
      }

      // Handle regular requests with auth
      if (auth) {
        const authHeaders = await this.getAuthHeaders();
        const headers = { ...authHeaders, ...customHeaders };
        setRequestContext(this.buildRequestContext(endpoint, requestMethod, headers));
        return this.executeRequest(endpoint, options, headers);
      }

      // Handle regular requests without auth
      const baseHeaders = await this.getHeaders();
      const headers = { ...baseHeaders, ...customHeaders };
      setRequestContext(this.buildRequestContext(endpoint, requestMethod, headers));
      return this.executeRequest(endpoint, options, headers);
    });
  }

  /**
   * Execute the actual fetch request with proper error handling
   */
  private async executeRequest(
    endpoint: string,
    options: RequestInit,
    headers: Record<string, string>
  ): Promise<Response> {
    const response = await fetch(this.formatUrl(endpoint), {
      ...options,
      headers: { ...headers, ...options.headers },
    });

    if (!response.ok) {
      await this.createApiError(response);
    }

    return response;
  }

  /**
   * GET request with realm headers for process-related endpoints
   */
  public async getWithRealm<_T>(
    endpoint: string,
    realm: string,
    customHeaders: Record<string, string> = {}
  ): Promise<Response> {
    const realmHeaders = {
      "x-provider-realm": realm,
      Realm: realm,
      ...customHeaders,
    };
    return this.get(endpoint, realmHeaders, true);
  }

  /**
   * POST request with realm headers for process-related endpoints
   */
  public async postWithRealm<T = Record<string, string>>(
    endpoint: string,
    data: T,
    realm: string,
    customHeaders: Record<string, string> = {}
  ): Promise<Response> {
    const realmHeaders = {
      "x-provider-realm": realm,
      Realm: realm,
      ...customHeaders,
    };
    return this.post(endpoint, data, realmHeaders, true);
  }

  /**
   * Helper method to convert fetch Response to axios-like response structure
   * for backward compatibility with existing code
   */
  public async toAxiosResponse<T = Record<string, string>>(
    response: Response
  ): Promise<ApiResponse<T>> {
    // Handle 204 No Content responses - they don't have a body
    const isNoContent = response.status === 204;
    const contentType = response.headers.get("content-type");
    const isJsonResponse = contentType?.includes("application/json");

    const data: T = isNoContent || !isJsonResponse ? (null as T) : await response.json();

    // Convert Headers to plain object for axios compatibility
    const headersObj: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headersObj[key] = value;
    });

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: headersObj,
      config: {},
      request: {},
    };
  }
}
